/**
 * User Status Operations Service - Phase 4 Architecture
 *
 * Encapsulates user suspension and status updates.
 * Extracted from UserService to reduce single-responsibility violations.
 */

import { typedSupabase } from "@/lib/typed-supabase";
import { logger } from "@/lib/logger";
import type { IService } from "@/services/core/service-registry";
import { ServiceRegistry } from "@/services/core/service-registry";
import { AuditService } from "./auditService";

export class UserStatusOperationsService implements IService {
  readonly name = "user-status-operations" as const;
  private readonly supabase = typedSupabase;
  private auditService?: AuditService;
  private registry: ServiceRegistry;

  constructor(serviceRegistry: ServiceRegistry) {
    this.registry = serviceRegistry;
  }

  async initialize(): Promise<void> {
    logger.info("🚦 UserStatusOperationsService initialized");
  }

  async dispose(): Promise<void> {
    logger.info("🚦 UserStatusOperationsService disposed");
  }

  private async ensureAudit(): Promise<AuditService> {
    this.auditService ??= await this.registry.get<AuditService>("audit");
    return this.auditService;
  }

  async toggleUserSuspension(userId: string, isSuspended: boolean): Promise<void> {
    const { error } = await this.supabase
      .from("profiles")
      .update({ is_suspended: isSuspended })
      .eq("id", userId);

    if (error) {
      throw new Error(`Failed to toggle user suspension: ${error.message}`);
    }

    try {
      await (await this.ensureAudit()).logAdminAction({
        action: isSuspended ? "suspend_user" : "unsuspend_user",
        entity_type: "user",
        entity_id: userId,
        details: { is_suspended: isSuspended },
      });
    } catch (auditErr) {
      logger.warn("Audit log failed for toggleUserSuspension", { userId, error: auditErr });
    }
  }

  async updateUserStatus(userId: string, isSuspended: boolean): Promise<{ success: boolean }> {
    const { error } = await this.supabase
      .from("profiles")
      .update({ is_suspended: isSuspended })
      .eq("id", userId);

    if (error) {
      throw new Error(`Failed to update user status: ${error.message}`);
    }

    try {
      await (await this.ensureAudit()).logAdminAction({
        action: isSuspended ? "suspend_user" : "unsuspend_user",
        entity_type: "user",
        entity_id: userId,
        details: { is_suspended: isSuspended, source: "updateUserStatus" },
      });
    } catch (auditErr) {
      logger.warn("Audit log failed for updateUserStatus", { userId, error: auditErr });
    }

    return { success: true };
  }
}
